import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
  useVerifyGstMutation,
  useVerifyPanMutation,
} from '@/features/auth/api/useAuthMutations';
import type {
  GstVerifyResponse,
  KycDetails,
  NormalizedField,
  PanVerifyResponse,
} from '@/features/auth/types/onboardingFields';
import { KYC } from '@/features/auth/types/onboardingFields';
import { fieldKey, hasValue } from '@/features/auth/utils/onboardingFields';
import {
  buildGstRows,
  buildPanRows,
  getPendingVerifyMessage,
  isFieldLocked,
  isKycFieldName,
  isKycFieldVerified,
  shouldShowVerifyButton,
} from '@/features/auth/utils/kycFieldLogic';

type KycStatus = { panVerified?: boolean; gstVerified?: boolean };

type UseProfileOverviewKycParams = {
  dynamicForm: Record<string, string>;
  setDynamicForm: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  visibleFields: NormalizedField[];
  kycStatus?: KycStatus;
  /** Snapshot of values when profile loaded (for API-verified comparison). */
  savedDetails: Record<string, string>;
  editing: boolean;
  userId?: string;
};

/**
 * Profile overview KYC: same PAN/GST verify APIs and lock/edit rules as onboarding.
 */
export function useProfileOverviewKyc({
  dynamicForm,
  setDynamicForm,
  visibleFields,
  kycStatus,
  savedDetails,
  editing,
  userId,
}: UseProfileOverviewKycParams) {
  const verifyPan = useVerifyPanMutation();
  const verifyGst = useVerifyGstMutation();

  const [verifiedValues, setVerifiedValues] = useState<Record<string, string>>(
    {},
  );
  const [editingFields, setEditingFields] = useState<Record<string, boolean>>(
    {},
  );
  /** Value when Verify was last shown/unlocked — button stays disabled until this changes. */
  const [verifyBaseline, setVerifyBaseline] = useState<Record<string, string>>(
    {},
  );
  const [panDetails, setPanDetails] = useState<KycDetails | null>(null);
  const [gstDetails, setGstDetails] = useState<KycDetails | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const companyFieldName = useMemo(
    () =>
      visibleFields.find((f) => fieldKey(f.fieldName) === KYC.COMPANY)
        ?.fieldName,
    [visibleFields],
  );

  // Seed verified map + verify baselines from API kycStatus when values match saved profile.
  useEffect(() => {
    if (!editing) {
      setEditingFields({});
      setFieldErrors({});
      setVerifyBaseline({});
      return;
    }
    const nextVerified: Record<string, string> = {};
    const nextBaseline: Record<string, string> = {};
    for (const f of visibleFields) {
      const key = fieldKey(f.fieldName);
      const value = String(dynamicForm[f.fieldName] || '').trim();
      nextBaseline[f.fieldName] = value;
      if (!value) continue;
      if (key === KYC.PAN && kycStatus?.panVerified && value === savedDetails[f.fieldName]) {
        nextVerified[f.fieldName] = value;
      }
      if (key === KYC.GST && kycStatus?.gstVerified && value === savedDetails[f.fieldName]) {
        nextVerified[f.fieldName] = value;
      }
    }
    setVerifiedValues(nextVerified);
    setVerifyBaseline(nextBaseline);
    // Only re-seed when entering edit / profile snapshot changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, kycStatus?.panVerified, kycStatus?.gstVerified, savedDetails]);

  const clearFieldError = useCallback((name: string) => {
    setFieldErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  }, []);

  const setFieldError = useCallback((name: string, message: string) => {
    setFieldErrors((prev) => ({ ...prev, [name]: message }));
  }, []);

  const checkShouldShowVerifyButton = useCallback(
    (name: string) =>
      shouldShowVerifyButton({
        name,
        currentValue: dynamicForm[name],
        editingFields,
        verifiedValues,
        kycStatus,
        savedDetails,
      }),
    [dynamicForm, editingFields, kycStatus, savedDetails, verifiedValues],
  );

  const isGstEditing = useMemo(
    () =>
      Object.keys(editingFields).some(
        (k) => fieldKey(k) === KYC.GST && editingFields[k],
      ),
    [editingFields],
  );

  const checkIsFieldLocked = useCallback(
    (name: string) =>
      isFieldLocked({
        name,
        currentValue: dynamicForm[name],
        gstDetails,
        isGstEditing,
        shouldShowVerify: checkShouldShowVerifyButton(name),
      }),
    [checkShouldShowVerifyButton, dynamicForm, gstDetails, isGstEditing],
  );

  const checkIsKycVerified = useCallback(
    (name: string) =>
      isKycFieldVerified({
        name,
        currentValue: dynamicForm[name],
        editingFields,
        verifiedValues,
        kycStatus,
        savedDetails,
      }),
    [dynamicForm, editingFields, kycStatus, savedDetails, verifiedValues],
  );

  const pendingKycFields = useMemo(() => {
    return visibleFields
      .filter((field) => {
        const key = fieldKey(field.fieldName);
        if (key !== KYC.PAN && key !== KYC.GST) return false;
        const value = dynamicForm[field.fieldName];
        const current = String(value || '').trim();
        const saved = String(savedDetails[field.fieldName] || '').trim();
        // No edit to the value → don't force re-verify / validation.
        if (current === saved) return false;
        if (key === KYC.GST && !hasValue(value)) return false;
        if (field.isRequired || hasValue(value)) {
          return !checkIsKycVerified(field.fieldName);
        }
        return false;
      })
      .map((field) => field.fieldLabel || field.fieldName);
  }, [checkIsKycVerified, dynamicForm, savedDetails, visibleFields]);

  const pendingVerification = pendingKycFields.length > 0;
  const pendingVerifyMessage = useMemo(
    () => getPendingVerifyMessage(pendingKycFields),
    [pendingKycFields],
  );

  const unlockVerifiedField = useCallback(
    (name: string) => {
      const key = fieldKey(name);
      const current = String(dynamicForm[name] || '').trim();
      setEditingFields((prev) => ({ ...prev, [name]: true }));
      setVerifyBaseline((prev) => ({ ...prev, [name]: current }));
      setVerifiedValues((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
      if (key === KYC.PAN) setPanDetails(null);
      if (key === KYC.GST) setGstDetails(null);
    },
    [dynamicForm],
  );

  const markFieldVerified = useCallback((name: string, value: string) => {
    setVerifiedValues((prev) => ({ ...prev, [name]: value }));
    setVerifyBaseline((prev) => ({ ...prev, [name]: value }));
    setEditingFields((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  }, []);

  /** Verify stays disabled until the value differs from the edit baseline. */
  const isVerifyDisabled = useCallback(
    (name: string) => {
      const current = String(dynamicForm[name] || '').trim();
      if (!current) return true;
      const baseline = String(verifyBaseline[name] ?? savedDetails[name] ?? '').trim();
      return current === baseline;
    },
    [dynamicForm, savedDetails, verifyBaseline],
  );

  const verifyPanNumber = async (name: string) => {
    try {
      const panValue = String(dynamicForm[name] || '');
      const res = (await verifyPan.mutateAsync({
        pan_number: panValue,
        pan_name: 'Advertiser',
        sessionId: userId || '',
      })) as PanVerifyResponse;
      setPanDetails(res?.data?.panDetails || null);
      markFieldVerified(name, panValue);
      clearFieldError(name);
      toast.success(res?.message || 'PAN verified');
    } catch {
      /* toasted in mutation */
    }
  };

  const verifyGstNumber = async (name: string) => {
    try {
      const gstValue = String(dynamicForm[name] || '');
      const res = (await verifyGst.mutateAsync({
        gst_number: gstValue,
        sessionId: userId || '',
      })) as GstVerifyResponse;
      const details =
        res?.data?.gstDetails || res?.data?.gst_details || null;
      setGstDetails(details);
      markFieldVerified(name, gstValue);
      clearFieldError(name);
      if (details?.legal_name_of_business && companyFieldName) {
        setDynamicForm((prev) => ({
          ...prev,
          [companyFieldName]: details.legal_name_of_business || '',
        }));
      }
      toast.success(res?.message || 'GST verified');
    } catch {
      /* toasted in mutation */
    }
  };

  const panRows = useMemo(() => buildPanRows(panDetails), [panDetails]);
  const gstRows = useMemo(() => buildGstRows(gstDetails), [gstDetails]);

  return {
    fieldErrors,
    setFieldError,
    clearFieldError,
    pendingVerification,
    pendingVerifyMessage,
    shouldShowVerifyButton: checkShouldShowVerifyButton,
    isKycField: isKycFieldName,
    isFieldLocked: checkIsFieldLocked,
    unlockVerifiedField,
    isVerifyDisabled,
    verifyPanNumber,
    verifyGstNumber,
    panRows,
    gstRows,
    verifyPanPending: verifyPan.isPending,
    verifyGstPending: verifyGst.isPending,
    isKycVerified: checkIsKycVerified,
  };
}
